import type {
  ApiDashboardElement,
  ApiItemType,
  ApiReturnSite,
} from '@highimpact-frontend/api-types';
import type { AxisType, DisplayerType } from '@highimpact-frontend/displayer-types';

import type { WidgetInstanceRegistry } from '../runtime/instanceRegistry/types';
import type {
  DashboardGridIdentity,
  DashboardRuntimeDispatch,
  DashboardScope,
  DashboardWidgetInstance,
} from '../state/dashboardRuntimeTypes';

// -- widget runtime api --

/**
 * Narrow runtime surface passed to federated remote widgets
 * instead of raw Redux selectors.
 */
export interface WidgetRuntimeApi {
  readonly getElement: (
    dashboardElementId: number
  ) => ApiDashboardElement | undefined;
  readonly getWidget: (
    widgetId: string
  ) => DashboardWidgetInstance | undefined;
  readonly getGridForElement: (
    dashboardElementId: number
  ) => DashboardGridIdentity | undefined;
  readonly dispatch: DashboardRuntimeDispatch;
  readonly checkPermission: (
    item: ApiItemType,
    extra?: Record<string, unknown>
  ) => boolean;
  readonly scope: DashboardScope;
  readonly site?: ApiReturnSite | null;
  readonly project_id?: number | null;
  readonly instanceRegistry: Pick<
    WidgetInstanceRegistry,
    'registerInstance' | 'unregisterInstance'
  >;
}

// -- config panel helpers (aligned with remote-widget-core) --

/** Displayer axis update payload used by remote config panels. */
export interface RemoteDisplayerAxisUpdate {
  readonly axis_id: number;
  readonly value: Partial<AxisType>;
}

/** Displayer update payload used by remote config panels. */
export interface RemoteDisplayerUpdateParams {
  readonly axes: readonly RemoteDisplayerAxisUpdate[];
  readonly displayerId: number;
  readonly displayer: DisplayerType;
}

/**
 * Builds the runtime API object passed into remote widget modules.
 * Phase 1a will wire a Redux-backed bridge; Phase 4 swaps to library runtime hooks.
 */
export const buildWidgetRuntimeApi = (params: {
  readonly getElement: WidgetRuntimeApi['getElement'];
  readonly getWidget: WidgetRuntimeApi['getWidget'];
  readonly getGridForElement: WidgetRuntimeApi['getGridForElement'];
  readonly dispatch: DashboardRuntimeDispatch;
  readonly checkPermission: WidgetRuntimeApi['checkPermission'];
  readonly scope: DashboardScope;
  readonly site?: ApiReturnSite | null;
  readonly project_id?: number | null;
  readonly instanceRegistry: WidgetRuntimeApi['instanceRegistry'];
}): WidgetRuntimeApi => ({
  getElement: params.getElement,
  getWidget: params.getWidget,
  getGridForElement: params.getGridForElement,
  dispatch: params.dispatch,
  checkPermission: params.checkPermission,
  scope: params.scope,
  site: params.site,
  project_id: params.project_id,
  instanceRegistry: params.instanceRegistry,
});

/** Placeholder bridge used until Phase 1a connects real store selectors. */
export const createUnimplementedWidgetRuntimeApi = (): WidgetRuntimeApi => {
  const throwNotReady = (): never => {
    throw new Error('WidgetRuntimeApi is not connected yet');
  };

  return {
    getElement: () => throwNotReady(),
    getWidget: () => throwNotReady(),
    getGridForElement: () => throwNotReady(),
    dispatch: () => throwNotReady(),
    checkPermission: () => throwNotReady(),
    scope: { project_id: null, site_id: null, grid_id: null },
    instanceRegistry: {
      registerInstance: () => throwNotReady(),
      unregisterInstance: () => throwNotReady(),
    },
  };
};
